/**
 * Journal Entry Form — Shared types, constants, and pure utility functions
 */

import { type Account } from '../../services/account-service'
import { type EntryType , type CreateJournalEntryLine } from '../../services/journal-entry-service'
import { type LocalJournalEntryLine } from '../../services/local-db'
import { toSmallestUnit, fromSmallestUnit, firstNonBlank } from '../../utils/formatters'

// ============================================================================
// Types
// ============================================================================

export interface EntryLine {
  id: string
  accountId: string
  accountCode: string
  accountName: string
  debit: string
  credit: string
  memo: string
  currency: string
  exchangeRate?: string
  baseDebit?: string
  baseCredit?: string
  projectId?: string
  costCenterId?: string
  lineMode?: 'account' | 'party'
  contactId?: string
  contactName?: string
  partyRole?: string
  partyPurpose?: string
}

export interface AccountGroup {
  type: string
  label: string
  accounts: Account[]
}

export const ACCOUNT_GROUPS_TEMPLATE: Array<{ type: string; label: string }> = [
  { type: 'ACCOUNT_TYPE_ASSET', label: 'Assets' },
  { type: 'ACCOUNT_TYPE_LIABILITY', label: 'Liabilities' },
  { type: 'ACCOUNT_TYPE_EQUITY', label: 'Equity' },
  { type: 'ACCOUNT_TYPE_REVENUE', label: 'Revenue' },
  { type: 'ACCOUNT_TYPE_EXPENSE', label: 'Expenses' },
]

// ============================================================================
// Pure utilities
// ============================================================================

export function isLineEmpty(
  line: { accountId?: string; debit?: string | number | null; credit?: string | number | null; memo?: string | null },
): boolean {
  return !line.accountId && !line.debit && !line.credit && !line.memo
}

export function makeEmptyLine(baseCurrency: string): EntryLine {
  return {
    id: crypto.randomUUID(),
    accountId: '',
    accountCode: '',
    accountName: '',
    debit: '',
    credit: '',
    memo: '',
    currency: baseCurrency,
    exchangeRate: undefined,
    baseDebit: '',
    baseCredit: '',
    projectId: '',
    costCenterId: '',
    lineMode: 'account',
  }
}

export function formatDateStr(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function groupAccounts(accountList: Account[]): AccountGroup[] {
  const groups: AccountGroup[] = ACCOUNT_GROUPS_TEMPLATE.map(t => ({
    ...t,
    accounts: [],
  }))
  for (const account of accountList) {
    const group = groups.find(g => g.type === account.accountType)
    if (group) {
      group.accounts.push(account)
    }
  }
  return groups.filter(g => g.accounts.length > 0)
}

function rankAccountMatch(
  account: Account,
  lower: string,
  compact: string,
): number | null {
  const code = account.code.toLowerCase()
  const name = account.name.toLowerCase()
  const nameCompact = name.replace(/\s+/g, '')
  const codeCompact = code.replace(/\s+/g, '')

  const checks: Array<() => boolean> = [
    () => code.startsWith(lower),
    () => name.startsWith(lower),
    () => name.split(/\s+/).some(w => w.startsWith(lower)),
    () => !!compact && (nameCompact.startsWith(compact) || codeCompact.startsWith(compact)),
    () => code.includes(lower) || name.includes(lower),
    () => !!compact && (nameCompact.includes(compact) || codeCompact.includes(compact)),
  ]
  for (let i = 0; i < checks.length; i++) {
    if (checks[i]?.()) {return i}
  }
  return null
}

export function filterAccounts(accounts: Account[], search: string): Account[] {
  if (!search) {
    return accounts
  }
  const lower = search.toLowerCase()
  const compact = lower.replace(/\s+/g, '')

  type Scored = { account: Account; rank: number }
  const scored: Scored[] = []

  for (const a of accounts) {
    const rank = rankAccountMatch(a, lower, compact)
    if (rank !== null) {scored.push({ account: a, rank })}
  }

  scored.sort((a, b) => a.rank - b.rank)
  return scored.map(s => s.account)
}

export function computeTotals(
  lines: EntryLine[],
  hasMultipleCurrencies: boolean,
  currencyView: 'transaction' | 'base',
): { debit: number; credit: number; isBalanced: boolean } {
  let debit = 0
  let credit = 0
  for (const line of lines) {
    if (hasMultipleCurrencies || currencyView === 'base') {
      debit += parseFloat(line.baseDebit ?? line.debit) || 0
      credit += parseFloat(line.baseCredit ?? line.credit) || 0
    } else {
      debit += parseFloat(line.debit) || 0
      credit += parseFloat(line.credit) || 0
    }
  }
  return { debit, credit, isBalanced: Math.abs(debit - credit) < 0.01 }
}

export function detectMultipleCurrencies(lines: EntryLine[], baseCurrency: string): boolean {
  const currencies = new Set(lines.map(l => l.currency).filter(Boolean))
  return currencies.size > 1 || (currencies.size === 1 && !currencies.has(baseCurrency))
}

function assignOptionalApiFields(line: EntryLine, baseCurrency: string, result: CreateJournalEntryLine): void {
  if (line.memo) {result.memo = line.memo}
  if (line.exchangeRate && line.currency && line.currency !== baseCurrency) {
    result.exchangeRate = line.exchangeRate
  }
  if (line.projectId) {result.projectId = line.projectId}
  if (line.costCenterId) {result.costCenterId = line.costCenterId}
  if (line.contactId) {result.contactId = line.contactId}
}

function buildApiLine(line: EntryLine, baseCurrency: string): CreateJournalEntryLine {
  const currency = line.currency || baseCurrency
  const result: CreateJournalEntryLine = {
    accountId: line.accountId,
    amount: {
      amount: toSmallestUnit(parseFloat(line.debit || line.credit) || 0, currency),
      currency,
    },
    isDebit: !!line.debit,
  }
  assignOptionalApiFields(line, baseCurrency, result)
  return result
}

export function buildApiLines(
  lines: EntryLine[],
  baseCurrency: string,
): CreateJournalEntryLine[] {
  return lines
    .filter(line => line.accountId && (line.debit || line.credit))
    .map(line => buildApiLine(line, baseCurrency))
}

function applyFxToLocalLine(
  line: EntryLine,
  lineCurrency: string,
  baseCurrency: string,
  result: LocalJournalEntryLine,
): void {
  if (!line.exchangeRate || lineCurrency === baseCurrency) {return}
  result.exchangeRate = line.exchangeRate
  if (line.baseDebit) {
    result.baseDebitAmount = toSmallestUnit(parseFloat(line.baseDebit) || 0, baseCurrency)
  }
  if (line.baseCredit) {
    result.baseCreditAmount = toSmallestUnit(parseFloat(line.baseCredit) || 0, baseCurrency)
  }
}

function applyPartyToLocalLine(line: EntryLine, result: LocalJournalEntryLine): void {
  if (line.lineMode !== 'party') {return}
  result.lineMode = 'party'
  if (line.partyRole) {result.partyRole = line.partyRole}
  if (line.partyPurpose) {result.partyPurpose = line.partyPurpose}
}

function buildLocalLine(
  line: EntryLine,
  index: number,
  baseCurrency: string,
): LocalJournalEntryLine {
  const lineCurrency = line.currency || baseCurrency
  const result: LocalJournalEntryLine = {
    id: line.id,
    lineNumber: index + 1,
    accountId: line.accountId,
    debitAmount: line.debit ? toSmallestUnit(parseFloat(line.debit) || 0, lineCurrency) : 0,
    creditAmount: line.credit ? toSmallestUnit(parseFloat(line.credit) || 0, lineCurrency) : 0,
    currency: lineCurrency,
  }
  applyFxToLocalLine(line, lineCurrency, baseCurrency, result)
  if (line.memo) {result.memo = line.memo}
  if (line.contactId) {result.contactId = line.contactId}
  applyPartyToLocalLine(line, result)
  return result
}

export function buildLocalLines(
  lines: EntryLine[],
  baseCurrency: string,
): LocalJournalEntryLine[] {
  return lines
    .filter(line => line.accountId && (line.debit || line.credit))
    .map((line, index) => buildLocalLine(line, index, baseCurrency))
}

interface EntryTotalAccumulator {
  currencies: Set<string>
  txnDebit: number
  txnCredit: number
  baseDebit: number
  baseCredit: number
  foreignCcy: string
  fxDebit: number
  fxCredit: number
  baseCcyDebit: number
  baseCcyCredit: number
}

function makeEntryTotalAccumulator(): EntryTotalAccumulator {
  return {
    currencies: new Set<string>(),
    txnDebit: 0,
    txnCredit: 0,
    baseDebit: 0,
    baseCredit: 0,
    foreignCcy: '',
    fxDebit: 0,
    fxCredit: 0,
    baseCcyDebit: 0,
    baseCcyCredit: 0,
  }
}

function accumulateForeignAmounts(
  acc: EntryTotalAccumulator,
  line: LocalJournalEntryLine,
  ccy: string,
): void {
  acc.foreignCcy = ccy
  acc.fxDebit += line.debitAmount || 0
  acc.fxCredit += line.creditAmount || 0
}

function accumulateBaseAmounts(
  acc: EntryTotalAccumulator,
  line: LocalJournalEntryLine,
): void {
  acc.baseCcyDebit += line.debitAmount || 0
  acc.baseCcyCredit += line.creditAmount || 0
}

function accumulateEntryLine(
  acc: EntryTotalAccumulator,
  line: LocalJournalEntryLine,
  baseCurrency: string,
): void {
  const ccy = line.currency || baseCurrency
  acc.currencies.add(ccy)
  acc.txnDebit += line.debitAmount || 0
  acc.txnCredit += line.creditAmount || 0
  acc.baseDebit += line.baseDebitAmount ?? line.debitAmount ?? 0
  acc.baseCredit += line.baseCreditAmount ?? line.creditAmount ?? 0
  if (ccy !== baseCurrency) {
    accumulateForeignAmounts(acc, line, ccy)
  } else {
    accumulateBaseAmounts(acc, line)
  }
}

function pickMultiCurrencyBaseTotal(acc: EntryTotalAccumulator): number {
  if (acc.baseCcyCredit > 0 && acc.baseCcyDebit === 0) {return acc.baseCcyCredit}
  if (acc.baseCcyDebit > 0 && acc.baseCcyCredit === 0) {return acc.baseCcyDebit}
  return Math.max(acc.baseDebit, acc.baseCredit)
}

export function computeEntryTotals(
  entryLines: LocalJournalEntryLine[],
  baseCurrency: string,
): {
  totalDebit: { amount: number; currency: string }
  totalCredit: { amount: number; currency: string }
  totalDebitBase: { amount: number; currency: string }
  totalCreditBase: { amount: number; currency: string }
} {
  const acc = makeEntryTotalAccumulator()
  for (const line of entryLines) {
    accumulateEntryLine(acc, line, baseCurrency)
  }

  if (acc.currencies.size > 1) {
    const fxTotal = acc.fxDebit || acc.fxCredit
    const baseTotal = pickMultiCurrencyBaseTotal(acc)
    return {
      totalDebit: { amount: fxTotal, currency: acc.foreignCcy },
      totalCredit: { amount: fxTotal, currency: acc.foreignCcy },
      totalDebitBase: { amount: baseTotal, currency: baseCurrency },
      totalCreditBase: { amount: baseTotal, currency: baseCurrency },
    }
  }

  const ccy = entryLines[0]?.currency ?? baseCurrency
  return {
    totalDebit: { amount: acc.txnDebit, currency: ccy },
    totalCredit: { amount: acc.txnCredit, currency: ccy },
    totalDebitBase: { amount: acc.baseDebit, currency: baseCurrency },
    totalCreditBase: { amount: acc.baseCredit, currency: baseCurrency },
  }
}

interface ApiLineInput {
  id: string
  accountId: string
  accountCode?: string
  accountName?: string
  isDebit: boolean
  memo?: string
  contactId?: string
  contactName?: string
  projectId?: string
  costCenterId?: string
  amount: {
    original: { amount: number | string; currency: string }
    base: { amount: number | string; currency: string }
    exchangeRate?: string
  }
}

function splitApiAmounts(
  line: ApiLineInput,
): { debit: string; credit: string; baseDebit: string; baseCredit: string } {
  const origAmount = fromSmallestUnit(
    Number(line.amount.original.amount),
    line.amount.original.currency,
  ).toString()
  const baseAmount = fromSmallestUnit(
    Number(line.amount.base.amount),
    line.amount.base.currency,
  ).toString()
  return {
    debit: line.isDebit ? origAmount : '',
    credit: line.isDebit ? '' : origAmount,
    baseDebit: line.isDebit ? baseAmount : '',
    baseCredit: line.isDebit ? '' : baseAmount,
  }
}

function mapApiLineToEntryLine(line: ApiLineInput, accountsList: Account[]): EntryLine {
  // The API already serializes accountCode/accountName per line. Prefer those
  // over the local accountsList lookup — the picker's list is filtered by
  // filterPartyAccounts(), so party-receivable accounts (e.g. 1200-C002) are
  // absent and the lookup would return undefined → blank cell. Use the API
  // values as the source of truth and fall back to the list only when the
  // line came from somewhere that didn't include them.
  const account = accountsList.find(a => a.id === line.accountId)
  const amounts = splitApiAmounts(line)
  return {
    id: line.id,
    accountId: line.accountId,
    accountCode: firstNonBlank(line.accountCode, account?.code) ?? '',
    accountName: firstNonBlank(line.accountName, account?.name) ?? '',
    debit: amounts.debit,
    credit: amounts.credit,
    memo: line.memo ?? '',
    currency: line.amount.original.currency,
    exchangeRate: line.amount.exchangeRate,
    baseDebit: amounts.baseDebit,
    baseCredit: amounts.baseCredit,
    projectId: line.projectId ?? '',
    costCenterId: line.costCenterId ?? '',
    lineMode: line.contactId ? 'party' : 'account',
    contactId: line.contactId,
    contactName: line.contactName,
  }
}

export function mapApiLinesToEntryLines(
  apiLines: ApiLineInput[],
  accountsList: Account[],
): EntryLine[] {
  return apiLines.map(line => mapApiLineToEntryLine(line, accountsList))
}

interface LocalLineInput {
  id: string
  accountId: string
  debitAmount?: number
  creditAmount?: number
  currency?: string
  exchangeRate?: string
  baseDebitAmount?: number
  baseCreditAmount?: number
  memo?: string
  contactId?: string
  lineMode?: string
  partyRole?: string
  partyPurpose?: string
}

function pickLocalBaseAmount(
  isForeign: boolean,
  baseAmount: number | undefined,
  txnAmount: number | undefined,
  lineCurrency: string,
  baseCurrency: string,
): string {
  if (isForeign && baseAmount) {
    return fromSmallestUnit(baseAmount, baseCurrency).toString()
  }
  if (txnAmount) {
    return fromSmallestUnit(txnAmount, lineCurrency).toString()
  }
  return ''
}

function mapLocalLineToEntryLine(
  line: LocalLineInput,
  accountsList: Account[],
  baseCurrency: string,
): EntryLine {
  const account = accountsList.find(a => a.id === line.accountId)
  const lineCurrency = line.currency ?? baseCurrency
  const isForeign = lineCurrency !== baseCurrency

  const baseDebit = pickLocalBaseAmount(
    isForeign, line.baseDebitAmount, line.debitAmount, lineCurrency, baseCurrency,
  )
  const baseCredit = pickLocalBaseAmount(
    isForeign, line.baseCreditAmount, line.creditAmount, lineCurrency, baseCurrency,
  )

  return {
    id: line.id,
    accountId: line.accountId,
    accountCode: account?.code ?? '',
    accountName: account?.name ?? '',
    debit: line.debitAmount ? fromSmallestUnit(line.debitAmount, lineCurrency).toString() : '',
    credit: line.creditAmount ? fromSmallestUnit(line.creditAmount, lineCurrency).toString() : '',
    memo: line.memo ?? '',
    currency: lineCurrency,
    exchangeRate: line.exchangeRate,
    baseDebit,
    baseCredit,
    projectId: '',
    costCenterId: '',
    lineMode: line.lineMode === 'party' ? 'party' : 'account',
    contactId: line.contactId,
    partyRole: line.partyRole,
    partyPurpose: line.partyPurpose,
  }
}

export function mapLocalLinesToEntryLines(
  localLines: LocalLineInput[],
  accountsList: Account[],
  baseCurrency: string,
): EntryLine[] {
  return localLines.map(line => mapLocalLineToEntryLine(line, accountsList, baseCurrency))
}

export { type EntryType }
